import { getAuthData } from "Utils/storage";

export type PrincipalType = "USER" | "SESSION";
export type PrincipalSource = "BROWSER" | "MOBILE";

export type ConsentPrincipal = {
  id: string;
  type: PrincipalType;
  source?: PrincipalSource;
};

const GUEST_KEY = "nc_consent_guest_id";
const SESSION_ID = /^[gd]_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    return (char === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
}

function readCookie(name: string) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
}

function browserGuestId() {
  if (typeof localStorage === "undefined") return "";
  const stored = localStorage.getItem(GUEST_KEY) || readCookie(GUEST_KEY);
  if (stored && SESSION_ID.test(stored)) return stored;
  const created = `g_${uuid()}`;
  localStorage.setItem(GUEST_KEY, created);
  writeCookie(GUEST_KEY, created);
  return created;
}

function mobileSessionId(deviceId: string) {
  const trimmed = deviceId.trim();
  if (!trimmed) return "";
  if (SESSION_ID.test(trimmed)) return trimmed;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) return `d_${trimmed}`;
  return `d_${uuid()}`;
}

export function resolvePrincipal(input: { deviceId?: string | null; userIdParam?: string | null } = {}): ConsentPrincipal {
  const auth = getAuthData() as { systemUserId?: string | number } | null;
  if (auth?.systemUserId != null && String(auth.systemUserId).trim()) {
    return { id: String(auth.systemUserId), type: "USER" };
  }
  const userIdParam = input.userIdParam?.trim() || "";
  if (userIdParam) return { id: userIdParam, type: "USER" };
  const deviceId = input.deviceId?.trim() || "";
  if (deviceId) return { id: mobileSessionId(deviceId), type: "SESSION", source: "MOBILE" };
  const guestId = browserGuestId();
  return { id: guestId, type: "SESSION", source: "BROWSER" };
}

export function principalHeaders(principal: ConsentPrincipal): Record<string, string> {
  return {
    "x-nc-principal-id": principal.id,
    "x-nc-principal-type": principal.type,
    ...(principal.source ? { "x-nc-principal-source": principal.source } : {}),
  };
}

export function isSessionPrincipal(principal: ConsentPrincipal | { id?: string; type?: string } | null | undefined) {
  if (!principal) return false;
  if (principal.type === "SESSION") return true;
  return !!principal.id && SESSION_ID.test(principal.id);
}
